import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportsService } from 'src/app/services/reports.service';
import { SqlReport } from 'src/app/shared/models/reports/sql-report.model';

import { SqlReportResult } from 'src/app/shared/models/reports/sql-report-result.model';

@Component({
  selector: 'app-sql-reports',
  templateUrl: './sql-reports.component.html',
  styleUrls: ['./sql-reports.component.scss'],
})
export class SqlReportsComponent {
  @ViewChild('paramModal') paramModal: any;

  showError: boolean = false;
  errorResponse: HttpErrorResponse;
  isBusy: boolean = false;

  reports: SqlReport[] = [];
  selectedReport: SqlReport | null = null;
  parameterValues: { [key: string]: any } = {};

  ngOnInit(): void {
    this.loadReports();
  }

  constructor(public reportsService: ReportsService) {}

  loadReports() {
    this.isBusy = true;
    this.showError = false;
    this.reportsService.getReports().subscribe({
      next: (reports: SqlReport[]) => {
        this.reports = reports;
        this.isBusy = false;
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
        this.isBusy = false;
      },
    });
  }

  onDownloadClick(report: SqlReport) {
    this.selectedReport = report;

    // If no parameters, execute immediately
    if (!report.parameters || report.parameters.length === 0) {
      this.executeReport({});
    } else {
      // Open modal for parameter input
      this.parameterValues = {};
      // Initialize parameter values to ensure form controls are registered correctly
      if (report.parameters) {
        report.parameters.forEach((param) => {
          this.parameterValues[param.name] = param.type === 'bool' ? false : '';
        });
      }
      this.paramModal.show();
    }
  }

  onModalSubmit() {
    // Validate required fields
    if (this.selectedReport) {
      for (const param of this.selectedReport.parameters) {
        const value = this.parameterValues[param.name];
        const isMissing = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

        if (param.required && isMissing) {
          this.showError = true;
          this.errorResponse = new HttpErrorResponse({
            status: 400,
            statusText: 'Bad Request',
            error: `Please fill in required field: ${param.name}`,
          });
          return;
        }
      }

      this.paramModal.hide();
      this.executeReport(this.parameterValues);
    }
  }

  onModalCancel() {
    this.paramModal.hide();
    setTimeout(() => {
      this.selectedReport = null;
      this.parameterValues = {};
    }, 300); // Wait for modal animation
  }

  executeReport(parameters: any) {
    if (!this.selectedReport) return;

    this.isBusy = true;
    this.showError = false;

    // Convert parameter values based on type
    const convertedParams: any = {};
    if (this.selectedReport.parameters) {
      for (const param of this.selectedReport.parameters) {
        const value = parameters[param.name];
        // Only include if value is provided (not empty string for non-required params)
        if (value !== undefined && value !== null && value !== '') {
          convertedParams[param.name] = this.convertParameterValue(value, param.type);
        } else if (param.type === 'bool') {
          convertedParams[param.name] = !!value;
        }
      }
    }

    this.reportsService.executeReport(this.selectedReport.reportName, convertedParams).subscribe({
      next: (result: SqlReportResult) => {
        this.downloadCsv(result.csvData, result.fileName);
        this.isBusy = false;
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
        this.isBusy = false;
      },
    });
  }

  convertParameterValue(value: any, type: string): any {
    switch (type) {
      case 'int': {
        const normalized = typeof value === 'string' ? value.trim() : value;
        const parsed = parseInt(normalized, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }
      case 'decimal': {
        const normalized = typeof value === 'string' ? value.trim() : value;
        const parsed = parseFloat(normalized);
        return Number.isNaN(parsed) ? null : parsed;
      }
      case 'bool':
        return value === true || value === 'true';
      case 'datetime':
        // Convert to ISO string if it's a date input
        if (value instanceof Date) {
          return value.toISOString();
        }
        if (typeof value === 'string' && value.trim() !== '') {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
        return value;
      default:
        return value;
    }
  }

  downloadCsv(csvData: string, fileName: string) {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 0);
  }

  getInputType(paramType: string): string {
    const typeMap: { [key: string]: string } = {
      datetime: 'datetime-local',
      int: 'number',
      decimal: 'number',
      string: 'text',
      bool: 'checkbox',
    };
    return typeMap[paramType] || 'text';
  }

  getInputStep(paramType: string): string | null {
    if (paramType === 'int') return '1';
    if (paramType === 'decimal') return '0.01';
    return null;
  }
}
